import { useEffect, useMemo, useState } from 'react';
import { tasksApi, type TaskWorkloadPreview, type TaskWorkloadPreviewParams } from '../api/tasks';
import {
  formatTaskApiError,
  type WorkloadPreviewMap,
} from '../utils/taskWorkloadCapacity';

type WorkloadPreviewLoader = (
  params: TaskWorkloadPreviewParams & { exclude_todo_id?: string | null },
) => Promise<{ data: TaskWorkloadPreview }>;

export function useTaskWorkloadPreviews({
  enabled,
  employeeIds,
  capacityDate,
  excludeTaskId,
  excludeTodoId,
  previewLoader = tasksApi.getWorkloadPreview,
}: {
  enabled: boolean;
  employeeIds: string[];
  capacityDate: string;
  excludeTaskId?: string | null;
  excludeTodoId?: string | null;
  previewLoader?: WorkloadPreviewLoader;
}): WorkloadPreviewMap {
  const employeeKey = useMemo(
    () => Array.from(new Set(employeeIds.filter(Boolean))).sort().join('|'),
    [employeeIds],
  );
  const [previews, setPreviews] = useState<WorkloadPreviewMap>({});

  useEffect(() => {
    const ids = employeeKey ? employeeKey.split('|') : [];
    if (!enabled || ids.length === 0 || !capacityDate) {
      setPreviews({});
      return;
    }

    setPreviews(() => {
      const next: WorkloadPreviewMap = {};
      for (const id of ids) {
        next[id] = { status: 'loading' };
      }
      return next;
    });

    let cancelled = false;
    const timer = window.setTimeout(() => {
      ids.forEach((employeeId) => {
        Promise.resolve(previewLoader({
            employee_id: employeeId,
            date: capacityDate,
            exclude_task_id: excludeTaskId || undefined,
            exclude_todo_id: excludeTodoId || undefined,
          }))
          .then((res) => {
            if (cancelled) return;
            if (!res?.data) {
              throw new Error('Workload cannot be calculated for this employee.');
            }
            setPreviews((prev) => ({
              ...prev,
              [employeeId]: { status: 'ready', data: res.data },
            }));
          })
          .catch((error) => {
            if (cancelled) return;
            setPreviews((prev) => ({
              ...prev,
              [employeeId]: {
                status: 'error',
                error: formatTaskApiError(
                  error,
                  'Workload cannot be calculated for this employee.',
                ),
              },
            }));
          });
      });
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [enabled, employeeKey, capacityDate, excludeTaskId, excludeTodoId, previewLoader]);

  return previews;
}
